import type { AssetFileMeta } from "@greg-chick/shared";
import { inspectFile } from "./cutoutFile";

export interface UploadMeta extends AssetFileMeta {
  dataUrl: string;
  name: string;
  /** 가장자리가 흰색인가 — 「배경 지우기」를 권할지 판단한다(못 봤으면 undefined). */
  edgeIsWhite?: boolean;
}

const KNOWN_EXT = new Set(["webp", "png", "jpg", "jpeg", "mp4", "webm", "mp3", "m4a", "ogg", "wav"]);

/** 파일명/타입에서 포맷 추출(소문자, jpeg→jpg). 이미지·동영상·음성.
 *  오디오는 MIME 우선(audio/mp4=m4a vs video/mp4=mp4 구분, 엣지 fmtFromContentType 과 일치).
 *  이미지·동영상은 알려진 확장자 우선(확장자가 더 명확). 순수·테스트 가능. */
export function extFormat(name: string, type: string): string {
  const t = type.toLowerCase();
  if (t.startsWith("audio/")) {
    const a = t.slice(6).split(";")[0]!.trim();
    if (a === "mpeg") return "mp3";
    if (a === "mp4" || a === "x-m4a") return "m4a";
    if (a === "x-wav" || a === "wave") return "wav";
    return a; // ogg, wav, mp3
  }
  const ext = (name.split(".").pop() ?? "").toLowerCase();
  const fromType = t.replace(/^(image|video)\//, "");
  const f = KNOWN_EXT.has(ext) ? ext : fromType || ext;
  return f === "jpeg" ? "jpg" : f;
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error("read_error"));
    r.readAsDataURL(file);
  });
}

function imageDims(dataUrl: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 0, height: 0 });
    img.src = dataUrl;
  });
}

/** 업로드 파일 → 검증·미리보기용 메타. 이미지가 아니면 width/height 0. */
export async function readUpload(file: File): Promise<UploadMeta> {
  const dataUrl = await fileToDataUrl(file);
  const isImg = /^image\//.test(file.type) || /\.(png|jpe?g|webp)$/i.test(file.name);
  const dims = isImg ? await imageDims(dataUrl) : { width: 0, height: 0 };
  // 배경이 남았는지 **픽셀을 실제로 본다.** 형식만으로는 알 수 없다 —
  // AI 가 만든 PNG 는 흰 배경이 칠해진 채로 나온다(docs/build/28 §13).
  const bg = isImg ? await inspectFile(dataUrl) : undefined;
  return {
    name: file.name,
    format: extFormat(file.name, file.type),
    bytes: file.size,
    width: dims.width || undefined,
    height: dims.height || undefined,
    hasBackground: bg?.hasBackground,
    edgeIsWhite: bg?.edgeIsWhite,
    dataUrl,
  };
}
